import React, { useEffect, useState, useCallback } from 'react';
import config from '../../config';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Switch,
  FormControlLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const userObj = JSON.parse(sessionStorage.getItem('user') || '{}');
const userId = userObj?.id || userObj?.userId || 0;

// Channel Manager (Saasaro) admin — full integration console.
//   Tab 1  Connection & Mapping : credentials + our-room <-> CM-room map.
//   Tab 2  OTA Bookings         : bookings the CM pushed to our webhook; import.
//   Tab 3  Push / Sync          : send inventory / rate / stop-sell to the CM.
// Everything is scoped by (companyId, hotelId).
export default function ChannelManager() {
  const token = sessionStorage.getItem('token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const companyId = company?.id || 0;

  const [tab, setTab] = useState(0);
  const [hotels, setHotels] = useState([]);
  const [hotelId, setHotelId] = useState(0);

  const [cfg, setCfg] = useState({
    Provider: 'Saasaro',
    BaseUrl: '',
    ApiKey: '',
    HotelCode: '',
    WebhookSecret: '',
    IsEnabled: false,
  });

  const [localRoomTypes, setLocalRoomTypes] = useState([]);
  const [packages, setPackages] = useState([]);
  const [cmRatePlans, setCmRatePlans] = useState([]);
  const [rows, setRows] = useState([]);

  const [bookings, setBookings] = useState([]);
  const [push, setPush] = useState({
    LocalRoomTypeId: 0,
    DateFrom: '',
    DateTo: '',
    Inventory: '',
    Rate: '',
  });

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [savingMap, setSavingMap] = useState(false);
  const [loadingBk, setLoadingBk] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const toast = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  useEffect(() => {
    if (!companyId) return;
    fetch(`${config.API_BASE_URL}/Home/GetHotelsdropdown/${companyId}`, auth)
      .then((r) => r.json())
      .then((d) => {
        const list = Array.isArray(d) ? d : [];
        setHotels(list);
        if (list.length) setHotelId(list[0].id);
      })
      .catch(() => {});
    fetch(`${config.API_BASE_URL}/channel/roomtypes/${companyId}`, auth)
      .then((r) => r.json())
      .then((d) => setLocalRoomTypes(Array.isArray(d) ? d : []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const loadForHotel = useCallback(async () => {
    if (!companyId || !hotelId) return;
    setLoading(true);
    try {
      const [cRes, pRes, mRes] = await Promise.all([
        fetch(`${config.API_BASE_URL}/channel/config/${companyId}/${hotelId}`, auth),
        fetch(`${config.API_BASE_URL}/channel/packages/${companyId}/${hotelId}`, auth),
        fetch(`${config.API_BASE_URL}/channel/roommaps/${companyId}/${hotelId}`, auth),
      ]);
      const c = cRes.ok ? await cRes.json() : null;
      const p = pRes.ok ? await pRes.json() : [];
      const m = mRes.ok ? await mRes.json() : [];
      setCfg(
        c
          ? {
              Provider: c.provider || 'Saasaro',
              BaseUrl: c.baseUrl || '',
              ApiKey: c.apiKey || '',
              HotelCode: c.hotelCode || '',
              WebhookSecret: c.webhookSecret || '',
              IsEnabled: !!c.isEnabled,
            }
          : {
              Provider: 'Saasaro',
              BaseUrl: '',
              ApiKey: '',
              HotelCode: '',
              WebhookSecret: '',
              IsEnabled: false,
            },
      );
      setPackages(Array.isArray(p) ? p : []);
      const saved = Array.isArray(m) ? m : [];
      setRows(
        saved.map((x) => ({
          cmRoomTypeId: x.cmRoomTypeId,
          cmRoomTypeName: x.cmRoomTypeName,
          cmRatePlanId: x.cmRatePlanId || '',
          cmRatePlanName: x.cmRatePlanName || '',
          localRoomTypeId: x.localRoomTypeId || 0,
          localPackageId: x.localPackageId || 0,
        })),
      );
    } catch {
      /* non-fatal */
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, hotelId]);

  useEffect(() => {
    loadForHotel();
  }, [loadForHotel]);

  const loadBookings = useCallback(async () => {
    if (!companyId || !hotelId) return;
    setLoadingBk(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/channel/bookings/${companyId}/${hotelId}`,
        auth,
      );
      setBookings(res.ok ? await res.json() : []);
    } catch {
      setBookings([]);
    } finally {
      setLoadingBk(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, hotelId]);

  useEffect(() => {
    if (tab === 1) loadBookings();
  }, [tab, loadBookings]);

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/channel/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth.headers },
        body: JSON.stringify({ Companyid: companyId, HotelId: hotelId, ...cfg }),
      });
      const body = await res.json().catch(() => ({}));
      toast(body.message || (res.ok ? 'Saved.' : 'Save failed.'), res.ok ? 'success' : 'error');
    } catch {
      toast('Could not save configuration.', 'error');
    } finally {
      setSavingCfg(false);
    }
  };

  const fetchFromSaasaro = async () => {
    setFetching(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/channel/fetch/${companyId}/${hotelId}`,
        auth,
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body.success === false) {
        toast(body.message || 'Fetch failed.', 'error');
        return;
      }
      const cats = body.roomCategories || [];
      const plans = body.ratePlans || [];
      setCmRatePlans(plans);
      setRows((prev) => {
        const byId = {};
        prev.forEach((r) => (byId[String(r.cmRoomTypeId)] = r));
        return cats.map((c) => {
          const id = String(c.room_type_id);
          const ex = byId[id];
          return {
            cmRoomTypeId: id,
            cmRoomTypeName: c.room_category_name,
            cmRatePlanId: ex?.cmRatePlanId || '',
            cmRatePlanName: ex?.cmRatePlanName || '',
            localRoomTypeId: ex?.localRoomTypeId || 0,
            localPackageId: ex?.localPackageId || 0,
          };
        });
      });
      toast(`Fetched ${cats.length} room type(s) from ${body.hotelName || 'Saasaro'}.`);
    } catch {
      toast('Could not reach the channel manager.', 'error');
    } finally {
      setFetching(false);
    }
  };

  const setRow = (cmId, patch) =>
    setRows((prev) =>
      prev.map((r) => (r.cmRoomTypeId === cmId ? { ...r, ...patch } : r)),
    );

  const saveMaps = async () => {
    setSavingMap(true);
    try {
      const Maps = rows
        .filter((r) => r.localRoomTypeId && r.cmRoomTypeId)
        .map((r) => {
          const plan = cmRatePlans.find(
            (p) => String(p.rate_plan_id) === String(r.cmRatePlanId),
          );
          return {
            LocalRoomTypeId: Number(r.localRoomTypeId),
            LocalPackageId: Number(r.localPackageId || 0),
            CmRoomTypeId: String(r.cmRoomTypeId),
            CmRoomTypeName: r.cmRoomTypeName || '',
            CmRatePlanId: r.cmRatePlanId ? String(r.cmRatePlanId) : null,
            CmRatePlanName: plan?.rate_plan_name || r.cmRatePlanName || null,
            IsActive: true,
          };
        });
      const res = await fetch(`${config.API_BASE_URL}/channel/roommaps`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth.headers },
        body: JSON.stringify({ Companyid: companyId, HotelId: hotelId, Maps }),
      });
      const body = await res.json().catch(() => ({}));
      toast(body.message || (res.ok ? 'Mapping saved.' : 'Save failed.'), res.ok ? 'success' : 'error');
    } catch {
      toast('Could not save mapping.', 'error');
    } finally {
      setSavingMap(false);
    }
  };

  const importBooking = async (id) => {
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/channel/bookings/${id}/import?userId=${userId}`,
        { method: 'POST', ...auth },
      );
      const body = await res.json().catch(() => ({}));
      toast(body.message || (res.ok ? 'Imported.' : 'Import failed.'), res.ok ? 'success' : 'error');
      loadBookings();
    } catch {
      toast('Import failed.', 'error');
    }
  };

  const doPush = async (kind) => {
    if (!push.LocalRoomTypeId || !push.DateFrom || !push.DateTo) {
      toast('Pick a room type and a date range first.', 'warning');
      return;
    }
    setPushing(true);
    try {
      const path =
        kind === 'inventory'
          ? 'push/inventory'
          : kind === 'rate'
            ? 'push/rate'
            : 'push/stopsell';
      const body = {
        Companyid: companyId,
        HotelId: hotelId,
        LocalRoomTypeId: Number(push.LocalRoomTypeId),
        DateFrom: push.DateFrom,
        DateTo: push.DateTo,
        Inventory: kind === 'inventory' ? Number(push.Inventory || 0) : null,
        Rate: kind === 'rate' ? Number(push.Rate || 0) : null,
        Status: kind === 'close' ? 'Close' : kind === 'open' ? 'Open' : null,
      };
      const res = await fetch(`${config.API_BASE_URL}/channel/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth.headers },
        body: JSON.stringify(body),
      });
      const r = await res.json().catch(() => ({}));
      // Surface the channel manager's OWN response text on failure — a 422 is a
      // validation error whose detail (which field it rejected) lives here.
      const detail = r.rawResponse ? ` — ${r.rawResponse}` : '';
      toast(
        (r.message || (r.success ? 'Pushed.' : 'Push failed.')) + (r.success ? '' : detail),
        r.success ? 'success' : 'error',
      );
    } catch {
      toast('Push failed.', 'error');
    } finally {
      setPushing(false);
    }
  };

  const ratePlansForRoom = (cmRoomId) =>
    cmRatePlans.filter((p) => String(p.room_type_id) === String(cmRoomId));

  const statusColor = (s) =>
    s === 'Imported'
      ? 'success'
      : s === 'Cancelled'
        ? 'error'
        : s === 'NeedsReview'
          ? 'warning'
          : 'default';

  const webhookUrl = `${config.API_BASE_URL}/channel/booking?key=${cfg.WebhookSecret || '<secret>'}`;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant='h5' sx={{ fontWeight: 700, mb: 0.5 }}>
        Channel Manager
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Connect this hotel to its Channel Manager (Saasaro): map rooms, receive
        OTA bookings, and push inventory / rates.
      </Typography>

      <TextField
        select
        size='small'
        label='Hotel'
        value={hotelId}
        onChange={(e) => setHotelId(parseInt(e.target.value, 10))}
        sx={{ minWidth: 220, mb: 2 }}>
        {hotels.length === 0 && (
          <MenuItem value={company?.id || 0}>{company?.name || 'Current'}</MenuItem>
        )}
        {hotels.map((h) => (
          <MenuItem key={h.id} value={h.id}>
            {h.name}
          </MenuItem>
        ))}
      </TextField>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label='Connection & Mapping' />
        <Tab label='OTA Bookings' />
        <Tab label='Push / Sync' />
      </Tabs>

      {/* ═══ TAB 1: Connection & Mapping ═══ */}
      {tab === 0 && (
        <>
          <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2 }} elevation={2}>
            <Typography sx={{ fontWeight: 600, mb: 1.5 }}>1. Connection</Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}>
              <TextField
                size='small'
                label='Base URL'
                placeholder='https://sandbox-cm.saasaro.com'
                value={cfg.BaseUrl}
                onChange={(e) => setCfg({ ...cfg, BaseUrl: e.target.value })}
              />
              <TextField
                size='small'
                label='Hotel Code'
                placeholder='demoHotelRitz'
                value={cfg.HotelCode}
                onChange={(e) => setCfg({ ...cfg, HotelCode: e.target.value })}
              />
              <TextField
                size='small'
                label='API Key'
                value={cfg.ApiKey}
                onChange={(e) => setCfg({ ...cfg, ApiKey: e.target.value })}
              />
              <TextField
                size='small'
                label='Webhook Secret (for booking push)'
                value={cfg.WebhookSecret}
                onChange={(e) => setCfg({ ...cfg, WebhookSecret: e.target.value })}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={cfg.IsEnabled}
                    onChange={(e) => setCfg({ ...cfg, IsEnabled: e.target.checked })}
                  />
                }
                label='Integration Enabled'
              />
              <Box sx={{ flex: 1 }} />
              <Button
                variant='contained'
                startIcon={<SaveIcon />}
                disabled={savingCfg || !hotelId}
                onClick={saveConfig}>
                {savingCfg ? 'Saving…' : 'Save Connection'}
              </Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant='caption' color='text.secondary'>
              Give this Booking-Push (webhook) URL to Saasaro:
            </Typography>
            <Box
              sx={{
                mt: 0.5,
                p: 1,
                bgcolor: 'action.hover',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: 12,
                overflowX: 'auto',
              }}>
              {webhookUrl}
            </Box>
          </Paper>

          <Paper sx={{ p: 2.5, borderRadius: 2 }} elevation={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <Typography sx={{ fontWeight: 600 }}>2. Room Mapping</Typography>
              <Box sx={{ flex: 1 }} />
              <Button
                variant='outlined'
                startIcon={<CloudSyncIcon />}
                disabled={fetching || !hotelId}
                onClick={fetchFromSaasaro}>
                {fetching ? 'Fetching…' : 'Fetch from Saasaro'}
              </Button>
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CircularProgress size={26} />
              </Box>
            ) : rows.length === 0 ? (
              <Typography variant='body2' color='text.secondary' sx={{ py: 2 }}>
                Save the connection, then "Fetch from Saasaro" to pull this
                hotel's room types.
              </Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Channel Room</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Your Room Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Channel Rate Plan</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Your Rate Plan</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.cmRoomTypeId} hover>
                        <TableCell>
                          <Typography variant='body2' sx={{ fontWeight: 600 }}>
                            {r.cmRoomTypeName || r.cmRoomTypeId}
                          </Typography>
                          <Chip
                            size='small'
                            label={`id: ${r.cmRoomTypeId}`}
                            sx={{ mt: 0.5, height: 18, fontSize: 10 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            size='small'
                            fullWidth
                            value={r.localRoomTypeId || 0}
                            onChange={(e) =>
                              setRow(r.cmRoomTypeId, {
                                localRoomTypeId: parseInt(e.target.value, 10),
                              })
                            }>
                            <MenuItem value={0}>— not mapped —</MenuItem>
                            {localRoomTypes.map((lt) => (
                              <MenuItem key={lt.id} value={lt.id}>
                                {lt.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            size='small'
                            fullWidth
                            value={r.cmRatePlanId || ''}
                            onChange={(e) =>
                              setRow(r.cmRoomTypeId, { cmRatePlanId: e.target.value })
                            }>
                            <MenuItem value=''>— any —</MenuItem>
                            {ratePlansForRoom(r.cmRoomTypeId).map((p) => (
                              <MenuItem key={p.rate_plan_id} value={String(p.rate_plan_id)}>
                                {p.rate_plan_name} ({p.meal_plan_name})
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            size='small'
                            fullWidth
                            value={r.localPackageId || 0}
                            onChange={(e) =>
                              setRow(r.cmRoomTypeId, {
                                localPackageId: parseInt(e.target.value, 10),
                              })
                            }>
                            <MenuItem value={0}>— none —</MenuItem>
                            {packages
                              .filter(
                                (p) =>
                                  !r.localRoomTypeId ||
                                  p.roomTypeId === r.localRoomTypeId,
                              )
                              .map((p) => (
                                <MenuItem key={p.packageId} value={p.packageId}>
                                  {p.packageName}
                                </MenuItem>
                              ))}
                          </TextField>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
            {rows.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant='contained'
                  startIcon={<SaveIcon />}
                  disabled={savingMap}
                  onClick={saveMaps}>
                  {savingMap ? 'Saving…' : 'Save Mapping'}
                </Button>
              </Box>
            )}
          </Paper>
        </>
      )}

      {/* ═══ TAB 2: OTA Bookings ═══ */}
      {tab === 1 && (
        <Paper sx={{ p: 2.5, borderRadius: 2 }} elevation={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Typography sx={{ fontWeight: 600 }}>
              OTA Bookings received from the channel manager
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              variant='outlined'
              size='small'
              startIcon={<RefreshIcon />}
              onClick={loadBookings}>
              Refresh
            </Button>
          </Box>
          <Divider sx={{ mb: 1.5 }} />
          {loadingBk ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress size={26} />
            </Box>
          ) : bookings.length === 0 ? (
            <Typography variant='body2' color='text.secondary' sx={{ py: 2 }}>
              No OTA bookings yet. Once Saasaro pushes a booking to the webhook
              URL, it appears here for review and import.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>OTA Ref</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Guest</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Dates</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookings.map((b) => (
                    <TableRow key={b.id} hover>
                      <TableCell>
                        <Typography variant='body2'>{b.otaBookingId || '-'}</Typography>
                        {b.channelName && (
                          <Chip size='small' label={b.channelName} sx={{ mt: 0.5, height: 18, fontSize: 10 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2'>{b.guestName || '-'}</Typography>
                        <Typography variant='caption' color='text.secondary'>
                          {b.guestMobile || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant='caption'>
                          {b.checkIn || '?'} → {b.checkOut || '?'}
                        </Typography>
                      </TableCell>
                      <TableCell>{b.amount != null ? b.amount : '-'}</TableCell>
                      <TableCell>
                        <Chip size='small' color={statusColor(b.status)} label={b.status} />
                        {b.errorMessage && (
                          <Typography variant='caption' color='error' display='block'>
                            {b.errorMessage}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {b.createdBookingId > 0 ? (
                          <Chip size='small' color='success' label={`#${b.createdBookingId}`} />
                        ) : b.status === 'Cancelled' ? (
                          '-'
                        ) : (
                          <Button
                            size='small'
                            variant='contained'
                            startIcon={<DownloadIcon />}
                            onClick={() => importBooking(b.id)}>
                            Import
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>
      )}

      {/* ═══ TAB 3: Push / Sync ═══ */}
      {tab === 2 && (
        <Paper sx={{ p: 2.5, borderRadius: 2 }} elevation={2}>
          <Typography sx={{ fontWeight: 600, mb: 1.5 }}>
            Push inventory / rate / stop-sell to the channel manager
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
              gap: 2,
              mb: 2,
            }}>
            <TextField
              select
              size='small'
              label='Room Type'
              value={push.LocalRoomTypeId}
              onChange={(e) =>
                setPush({ ...push, LocalRoomTypeId: parseInt(e.target.value, 10) })
              }>
              <MenuItem value={0}>— select —</MenuItem>
              {localRoomTypes.map((lt) => (
                <MenuItem key={lt.id} value={lt.id}>
                  {lt.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size='small'
              type='date'
              label='From'
              InputLabelProps={{ shrink: true }}
              value={push.DateFrom}
              onChange={(e) => setPush({ ...push, DateFrom: e.target.value })}
            />
            <TextField
              size='small'
              type='date'
              label='To'
              InputLabelProps={{ shrink: true }}
              value={push.DateTo}
              onChange={(e) => setPush({ ...push, DateTo: e.target.value })}
            />
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
              mb: 2,
            }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size='small'
                type='number'
                label='Inventory'
                value={push.Inventory}
                onChange={(e) => setPush({ ...push, Inventory: e.target.value })}
                sx={{ flex: 1 }}
              />
              <Button variant='contained' disabled={pushing} onClick={() => doPush('inventory')}>
                Push
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size='small'
                type='number'
                label='Rate'
                value={push.Rate}
                onChange={(e) => setPush({ ...push, Rate: e.target.value })}
                sx={{ flex: 1 }}
              />
              <Button variant='contained' disabled={pushing} onClick={() => doPush('rate')}>
                Push
              </Button>
            </Box>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant='outlined'
              color='error'
              disabled={pushing}
              onClick={() => doPush('close')}>
              Stop Sell (Close)
            </Button>
            <Button
              variant='outlined'
              color='success'
              disabled={pushing}
              onClick={() => doPush('open')}>
              Open Room
            </Button>
          </Box>
        </Paper>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={9000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.sev} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
